import type { MistakeEntry } from '../types'

/**
 * 错题本 - 自动收集答错的卡片
 * 类似 ProgressTracker, 纯内存数据, 持久化由 store 负责
 */
export class MistakeBook {
  private entries: Map<string, MistakeEntry> = new Map()

  /**
   * 记录一次答错
   * 同一选项不再重复记录 (wrongChoices 去重)
   */
  recordMistake(cardId: string, choiceIndex: number): void {
    const now = Date.now()
    const existing = this.entries.get(cardId)

    if (existing) {
      existing.errorCount++
      existing.lastWrongAt = now
      // wrongChoices 去重
      if (!existing.wrongChoices.includes(choiceIndex)) {
        existing.wrongChoices.push(choiceIndex)
      }
    } else {
      this.entries.set(cardId, {
        cardId,
        errorCount: 1,
        wrongChoices: [choiceIndex],
        lastWrongAt: now,
        firstWrongAt: now,
      })
    }
  }

  /**
   * 标记"已掌握" - 从错题本移除某张卡
   * 答对时 + mastery 达到 60 时由 store 决定调用
   */
  removeMistake(cardId: string): void {
    this.entries.delete(cardId)
  }

  /**
   * 获取所有错题
   */
  getAll(): MistakeEntry[] {
    return Array.from(this.entries.values())
  }

  /**
   * 按"错题严重度"排序: 错题次数倒序 + 最近答错时间倒序
   */
  getRanked(): MistakeEntry[] {
    return this.getAll().sort((a, b) => {
      if (a.errorCount !== b.errorCount) return b.errorCount - a.errorCount
      return b.lastWrongAt - a.lastWrongAt
    })
  }

  /**
   * 获取错题数量
   */
  size(): number {
    return this.entries.size
  }

  /**
   * 清空所有错题（用于重置/清空学习数据）
   */
  reset(): void {
    this.entries = new Map()
  }

  /**
   * 从存档恢复
   */
  loadFromSave(data: Record<string, MistakeEntry> | undefined): void {
    this.entries = new Map(Object.entries(data ?? {}))
  }

  /**
   * 导出为存档格式
   */
  toSaveData(): Record<string, MistakeEntry> {
    return Object.fromEntries(this.entries)
  }
}